/**
 * Client-side impersonation state, stored in localStorage so a tab reload
 * keeps the preview active. Three pieces of state:
 *   - targetUserId: the user we're previewing as
 *   - targetName: pre-resolved display name so the banner renders instantly
 *   - targetRole: pre-resolved role so the banner can show "tech / manager"
 *
 * Read synchronously by the tRPC link + a React hook. Changes publish a
 * storage event so multiple open tabs stay in sync.
 */

const KEY = 'fw:impersonate:v1';
const EVENT = 'fw:impersonation:changed';

export interface ImpersonationState {
  targetUserId: string;
  targetName: string;
  targetRole: string;
}

export function readImpersonation(): ImpersonationState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationState;
    if (!parsed?.targetUserId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setImpersonation(next: ImpersonationState | null): void {
  if (typeof window === 'undefined') return;
  if (next) {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } else {
    window.localStorage.removeItem(KEY);
  }
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function onImpersonationChange(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const sameTab = () => cb();
  const crossTab = (e: StorageEvent) => {
    if (e.key === KEY) cb();
  };
  window.addEventListener(EVENT, sameTab);
  window.addEventListener('storage', crossTab);
  return () => {
    window.removeEventListener(EVENT, sameTab);
    window.removeEventListener('storage', crossTab);
  };
}
